import React, { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { Sentry } from "@/core/sentry";
import { isReportedError } from "@/core/errors";

export type ErrorBoundaryLevel = "root" | "app" | "layout" | "component";

export interface FallbackProps {
  error: Error;
  resetError: () => void;
  level: ErrorBoundaryLevel;
}

export interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: React.ComponentType<FallbackProps>;
  level: ErrorBoundaryLevel;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetKeys?: Array<string | number>;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary component that catches React errors and reports them to Sentry.
 * Supports multiple levels (root, app, layout, component) with different fallback UIs.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { level, onError } = this.props;

    // Only report to Sentry if not already reported
    if (!isReportedError(error)) {
      Sentry.withScope((scope) => {
        scope.setLevel("error");
        scope.setTag("error_boundary", level);
        scope.setContext("react_error_info", {
          componentStack: errorInfo.componentStack,
        });
        scope.setContext("error_boundary_info", {
          level,
          errorMessage: error.message,
          errorName: error.name,
        });
        Sentry.captureException(error);
      });

      // Log to console for development visibility
      console.error(`[ErrorBoundary:${level}] Caught error:`, error, errorInfo);
    } else {
      console.log(`[ErrorBoundary:${level}] Error already reported to Sentry, skipping duplicate report`);
    }

    // Call custom error handler if provided
    onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    const { resetKeys } = this.props;
    const { hasError } = this.state;

    // Reset error boundary when resetKeys change
    if (hasError && resetKeys && prevProps.resetKeys) {
      const hasResetKeyChanged = resetKeys.some((key, index) => key !== prevProps.resetKeys?.[index]);
      if (hasResetKeyChanged) {
        this.resetError();
      }
    }
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render() {
    const { hasError, error } = this.state;
    const { children, fallback: FallbackComponent, level } = this.props;

    if (hasError && error) {
      return <FallbackComponent error={error} resetError={this.resetError} level={level} />;
    }

    return children;
  }
}
